import { useState, useMemo } from 'react'
import { generateVillagers } from '@/economy/villagers'
import { directBuy, sellToMarket, getMarketPrices } from '@/economy/api'
import { ITEM_DISPLAY_NAMES, formatFollis } from '@/economy/types'
import { useEconomyStore } from '@/state/useEconomyStore'
import type { WalletInfo, ItemType } from '@/economy/types'
import type { InteractionTarget } from '@/state/useEconomyStore'

interface PanelProps { interaction: InteractionTarget; wallet: WalletInfo | null }

const BULK_DISCOUNT = 0.85  // 15% discount for bulk buying

export function BarnPanel({ interaction, wallet }: PanelProps) {
  const [tab, setTab] = useState<'stock' | 'buy' | 'sell'>('stock')
  const [selectedItem, setSelectedItem] = useState<ItemType | ''>('')
  const [quantity, setQuantity] = useState(5)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const villageData = useEconomyStore((s) => s.villageData.get(interaction.villageIndex))
  const updateCargoFromTrade = useEconomyStore((s) => s.updateCargoFromTrade)
  const setMarketPrices = useEconomyStore((s) => s.setMarketPrices)

  const prices = villageData?.marketPrices ?? []
  const priceMap = new Map(prices.map((p) => [p.item, p]))

  const aggregateStock = useMemo(() => {
    if (!interaction.profileType) return []
    const seed = 12345 + interaction.villageIndex * 777
    const sizes: Array<'small' | 'medium' | 'large'> = ['small', 'medium', 'large']
    const size = sizes[interaction.villageIndex % 3]
    const houseCounts = { small: 8, medium: 14, large: 22 }
    const villagers = generateVillagers(seed, size, interaction.profileType, houseCounts[size], prices.length > 0 ? prices : null)

    const totals = new Map<ItemType, number>()
    for (const v of villagers) {
      for (const [item, qty] of Object.entries(v.inventory)) {
        if ((qty as number) > 0) {
          totals.set(item as ItemType, (totals.get(item as ItemType) || 0) + (qty as number))
        }
      }
    }
    return Array.from(totals.entries()).sort((a, b) => b[1] - a[1])
  }, [interaction.villageIndex, interaction.profileType, prices])

  const cargoMap = new Map(wallet?.cargo ?? [])

  async function handleBulkBuy() {
    if (!selectedItem || interaction.serverId === null) return
    setLoading(true)
    setError(null)
    try {
      const result = await directBuy(interaction.serverId, selectedItem, quantity, BULK_DISCOUNT)
      updateCargoFromTrade(selectedItem, result.new_item_qty, result.new_follis_balance)
      const newPrices = await getMarketPrices(interaction.serverId)
      setMarketPrices(interaction.villageIndex, newPrices)
    } catch (e: any) {
      setError(e.message || 'Purchase failed')
    }
    setLoading(false)
  }

  async function handleBulkSell() {
    if (!selectedItem || interaction.serverId === null) return
    setLoading(true)
    setError(null)
    try {
      const result = await sellToMarket(interaction.serverId, selectedItem, quantity, 0)
      updateCargoFromTrade(selectedItem, result.new_item_qty, result.new_follis_balance)
      const newPrices = await getMarketPrices(interaction.serverId)
      setMarketPrices(interaction.villageIndex, newPrices)
    } catch (e: any) {
      setError(e.message || 'Sale failed')
    }
    setLoading(false)
  }

  const selectedPrice = selectedItem ? priceMap.get(selectedItem) : null
  const estimatedCost = selectedPrice && tab === 'buy'
    ? Math.ceil(selectedPrice.spot_price * quantity * BULK_DISCOUNT)
    : 0
  const estimatedRevenue = selectedPrice && tab === 'sell'
    ? Math.floor(selectedPrice.spot_price * quantity * 0.95)
    : 0

  return (
    <>
      <div className="panel-tabs">
        <button className={`panel-tab ${tab === 'stock' ? 'panel-tab--active' : ''}`} onClick={() => { setTab('stock'); setError(null) }}>Stockpile</button>
        <button className={`panel-tab ${tab === 'buy' ? 'panel-tab--active' : ''}`} onClick={() => { setTab('buy'); setSelectedItem(''); setError(null) }}>Bulk Buy</button>
        <button className={`panel-tab ${tab === 'sell' ? 'panel-tab--active' : ''}`} onClick={() => { setTab('sell'); setSelectedItem(''); setError(null) }}>Bulk Sell</button>
      </div>

      {error && <div className="panel-error">{error}</div>}

      {tab === 'stock' && (
        <div className="panel-section">
          <h3 className="panel-section__title">Village Stockpile</h3>
          {aggregateStock.length === 0 && <p className="panel-placeholder">Storehouse is empty.</p>}
          {aggregateStock.map(([item, qty]) => (
            <div key={item} className="panel-row">
              <span className="panel-row__label">{ITEM_DISPLAY_NAMES[item]}</span>
              <span className="panel-row__value">&times;{qty}</span>
            </div>
          ))}
        </div>
      )}

      {tab === 'buy' && (
        <>
          <div className="panel-section">
            <h3 className="panel-section__title">Wholesale (15% discount)</h3>
            {interaction.serverId === null && <p className="panel-placeholder">No active market.</p>}
            {prices.map((p) => (
              <div key={p.item} className="panel-row" style={{ cursor: 'pointer' }} onClick={() => setSelectedItem(p.item)}>
                <span className="panel-row__label" style={{ fontWeight: selectedItem === p.item ? 700 : 400 }}>
                  {ITEM_DISPLAY_NAMES[p.item]}
                </span>
                <span className="panel-row__value">
                  {formatFollis(Math.ceil(p.spot_price * BULK_DISCOUNT))}/ea
                </span>
              </div>
            ))}
          </div>
          {selectedItem && (
            <div className="panel-section">
              <h3 className="panel-section__title">Purchase</h3>
              <div className="panel-input-row">
                <span style={{ color: '#c4b99a', fontFamily: 'Crimson Text, serif', fontSize: 14 }}>
                  {ITEM_DISPLAY_NAMES[selectedItem]}
                </span>
                <span style={{ color: '#8b7355', fontSize: 12 }}>Qty:</span>
                <input
                  type="number"
                  className="panel-number-input"
                  value={quantity}
                  min={1}
                  max={100}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                />
              </div>
              <div className="panel-cost">&asymp; {formatFollis(estimatedCost)}</div>
              <button
                className="panel-btn panel-btn--primary"
                style={{ width: '100%' }}
                disabled={loading || !wallet || wallet.follis < estimatedCost}
                onClick={handleBulkBuy}
              >
                {loading ? 'Trading...' : `Buy ${quantity}× ${ITEM_DISPLAY_NAMES[selectedItem]}`}
              </button>
            </div>
          )}
        </>
      )}

      {tab === 'sell' && (
        <>
          <div className="panel-section">
            <h3 className="panel-section__title">Sell in Bulk</h3>
            {(!wallet || wallet.cargo.length === 0) && <p className="panel-placeholder">No items in cargo.</p>}
            {wallet?.cargo.map(([item, qty]) => (
              <div key={item} className="panel-row" style={{ cursor: 'pointer' }} onClick={() => { setSelectedItem(item); setQuantity(Math.min(qty, quantity || 5)) }}>
                <span className="panel-row__label" style={{ fontWeight: selectedItem === item ? 700 : 400 }}>
                  {ITEM_DISPLAY_NAMES[item]} &times;{qty}
                </span>
                <span className="panel-row__value">
                  {priceMap.get(item) ? `${priceMap.get(item)!.spot_price.toFixed(1)}ƒ/ea` : '—'}
                </span>
              </div>
            ))}
          </div>
          {selectedItem && cargoMap.has(selectedItem) && (
            <div className="panel-section">
              <h3 className="panel-section__title">Sell</h3>
              <div className="panel-input-row">
                <span style={{ color: '#c4b99a', fontFamily: 'Crimson Text, serif', fontSize: 14 }}>
                  {ITEM_DISPLAY_NAMES[selectedItem]}
                </span>
                <span style={{ color: '#8b7355', fontSize: 12 }}>Qty:</span>
                <input
                  type="number"
                  className="panel-number-input"
                  value={quantity}
                  min={1}
                  max={cargoMap.get(selectedItem) || 1}
                  onChange={(e) => setQuantity(Math.max(1, Math.min(cargoMap.get(selectedItem) || 1, parseInt(e.target.value) || 1)))}
                />
              </div>
              <div className="panel-cost">&asymp; {formatFollis(estimatedRevenue)} revenue</div>
              <button
                className="panel-btn panel-btn--primary"
                style={{ width: '100%' }}
                disabled={loading || quantity > (cargoMap.get(selectedItem) || 0)}
                onClick={handleBulkSell}
              >
                {loading ? 'Trading...' : `Sell ${quantity}× ${ITEM_DISPLAY_NAMES[selectedItem]}`}
              </button>
            </div>
          )}
        </>
      )}
    </>
  )
}
